import { supabase } from '../client/supabaseClient';

/**
 * Verify shop credentials and get shopAccountId
 */
export const verifyShopCredentials = async (email: string, secret: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
    .schema('api')
    .rpc('verify_shop_credentials', {
      p_email: email,
      p_secret: secret
    });

    if (error) {
      console.error('Error verifying credentials:', error);
      return null;
    }

    return data; // Returns the shopAccountId if valid
  } catch (error) {
    console.error('Exception during shop verification:', error);
    return null;
  }
};

/**
 * Check KYC status for a shop
 */
export const checkKYCStatus = async (shopAccountId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('shop_accounts')
      .select('kyc_status')
      .eq('id', shopAccountId)
      .single();

    if (error) {
      console.error('Error fetching KYC status:', error);
      return null;
    }

    return data?.kyc_status || null;
  } catch (error) {
    console.error('Exception during KYC status check:', error);
    return null;
  }
};

/**
 * Update KYC information for a shop
 */
export const updateShopKycInfo = async (
  shopAccountId: string, 
  kycData: {
    owner_name: string;
    gst_number: string;
    aadhaar: string;
    pan_card_path?: string;
    bank_proof_path?: string;
    passport_photo_path: string;
    account_holder_name?: string;
    account_number?: string;
    ifsc_code?: string;
    bank_name?: string;
    branch_name?: string;
    upi_id?: string;
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    // First check if KYC has already been submitted/verified
    const { data: statusData } = await supabase
      .from('shop_accounts')
      .select('kyc_status')
      .eq('id', shopAccountId)
      .single();

    if (statusData?.kyc_status === 'submitted' || statusData?.kyc_status === 'verified') {
      return { 
        success: false, 
        error: `KYC has already been ${statusData.kyc_status}. You cannot submit again.` 
      };
    }

    // Validate that at least one payment method is provided (either UPI or bank details)
    if (!kycData.upi_id && !kycData.account_number) {
      return {
        success: false,
        error: "You must provide either UPI ID or bank account details"
      };
    }

    const { data, error } = await supabase
    .schema('api')
    .rpc('update_shop_kyc_info', {
      p_shop_id: shopAccountId,
      p_owner_name: kycData.owner_name,
      p_gst_number: kycData.gst_number,
      p_aadhaar: kycData.aadhaar,
      p_pan_card_path: kycData.pan_card_path || null,
      p_bank_proof_path: kycData.bank_proof_path || null,
      p_passport_photo_path: kycData.passport_photo_path,
      p_account_holder_name: kycData.account_holder_name || null,
      p_account_number: kycData.account_number || null,
      p_ifsc_code: kycData.ifsc_code || null,
      p_bank_name: kycData.bank_name || null,
      p_branch_name: kycData.branch_name || null,
      p_upi_id: kycData.upi_id || null
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    return { success: false, error: errorMessage };
  }
};

export interface ShopInfo {
  shop_name: string;
  owner_name: string;
  kyc_status: string;
}

export const getShopInfo = async (shopAccountId: string): Promise<ShopInfo | null> => {
  try {
    const { data, error } = await supabase
      .from('shop_accounts')
      .select('shop_name, owner_name, kyc_status')
      .eq('id', shopAccountId)
      .single();

    if (error) {
      console.error('Error fetching shop info:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Unexpected error fetching shop info:', error);
    return null;
  }
};